// src/components/admin/DealerCarListingsManagement.jsx
// Admin panel to review, approve, price, and publish dealer-submitted cars

import React, { useState, useEffect, useCallback } from 'react';
import adminApi from '../../services/adminAuth';

const fmt = (n) => n && n > 0 ? `₹${Number(n).toLocaleString('en-IN')}` : '—';

const STATUS_CONFIG = {
  draft:     { label: 'Draft',          bg: '#f3f4f6', color: '#374151' },
  submitted: { label: 'Under Review',   bg: '#dbeafe', color: '#1d4ed8' },
  approved:  { label: 'Approved',       bg: '#fef3c7', color: '#92400e' },
  rejected:  { label: 'Rejected',       bg: '#fee2e2', color: '#991b1b' },
  published: { label: 'Published ✅',    bg: '#dcfce7', color: '#166534' },
};

export default function DealerCarListingsManagement() {
  const [listings,         setListings]         = useState([]);
  const [allListings,      setAllListings]       = useState([]);
  const [loading,          setLoading]           = useState(true);
  const [statusFilter,     setStatusFilter]      = useState('submitted');
  const [selectedListing,  setSelectedListing]   = useState(null);
  const [detailTab,        setDetailTab]         = useState('info');
  const [toast,            setToast]             = useState(null);
  const [actionLoading,    setActionLoading]     = useState(false);
  const [pricingForm,      setPricingForm]       = useState({ platform_price: '', platform_charges: '', inspection_charges: '' });
  const [rejectReason,     setRejectReason]      = useState('');

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const [currentRes, allRes] = await Promise.all([
        adminApi.get(`/admin/dealer_car_listings${params}`),
        adminApi.get('/admin/dealer_car_listings'),
      ]);
      setListings(currentRes.data?.data   || []);
      setAllListings(allRes.data?.data    || []);
    } catch {
      showToast('Failed to load listings', 'error');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const openDetail = (listing) => {
    setSelectedListing(listing);
    setDetailTab('info');
    setPricingForm({
      platform_price:     listing.platform_price     > 0 ? listing.platform_price     : '',
      platform_charges:   listing.platform_charges   > 0 ? listing.platform_charges   : '',
      inspection_charges: listing.inspection_charges > 0 ? listing.inspection_charges : '',
    });
    setRejectReason('');
  };

  const handleApprove = async (id) => {
    if (!window.confirm('Approve this listing? 1 credit will be deducted from the dealer.')) return;
    setActionLoading(true);
    try {
      await adminApi.put(`/admin/dealer_car_listings/${id}/approve`);
      showToast('Listing approved! Credit deducted from dealer.');
      load();
      setSelectedListing(null);
    } catch (err) {
      showToast(err.response?.data?.error || 'Approval failed', 'error');
    } finally { setActionLoading(false); }
  };

  const handleReject = async (id) => {
    if (!rejectReason.trim()) { showToast('Please enter a rejection reason', 'error'); return; }
    setActionLoading(true);
    try {
      await adminApi.put(`/admin/dealer_car_listings/${id}/reject`, { reason: rejectReason });
      showToast('Listing rejected.');
      load();
      setSelectedListing(null);
      setRejectReason('');
    } catch (err) {
      showToast(err.response?.data?.error || 'Rejection failed', 'error');
    } finally { setActionLoading(false); }
  };

  const handleSetPricing = async (id) => {
    if (!pricingForm.platform_price) { showToast('Platform price is required', 'error'); return; }
    setActionLoading(true);
    try {
      const res = await adminApi.put(`/admin/dealer_car_listings/${id}/set_pricing`, {
        platform_price:     parseFloat(pricingForm.platform_price),
        platform_charges:   parseFloat(pricingForm.platform_charges   || 0),
        inspection_charges: parseFloat(pricingForm.inspection_charges || 0),
      });
      showToast('Pricing set successfully!');
      setSelectedListing(res.data?.data || selectedListing);
      load();
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to set pricing', 'error');
    } finally { setActionLoading(false); }
  };

  const handleCreateCar = async (id) => {
    if (!window.confirm('Create Car record from this listing? You can then add specs/features and publish.')) return;
    setActionLoading(true);
    try {
      const res = await adminApi.post(`/admin/dealer_car_listings/${id}/create_car`);
      showToast(`Car #${res.data.car_id} created! Now add specs and publish.`);
      load();
      setSelectedListing(prev => prev ? { ...prev, car_id: res.data.car_id } : null);
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to create car', 'error');
    } finally { setActionLoading(false); }
  };

  const handlePublish = async (id) => {
    if (!window.confirm('Publish this car? It will go live on the platform.')) return;
    setActionLoading(true);
    try {
      await adminApi.put(`/admin/dealer_car_listings/${id}/publish`);
      showToast('Car published and live!');
      load();
      setSelectedListing(null);
    } catch (err) {
      showToast(err.response?.data?.error || 'Publish failed', 'error');
    } finally { setActionLoading(false); }
  };

  const FILTERS = ['all', 'submitted', 'approved', 'rejected', 'published'];
  const counts  = FILTERS.reduce((acc, s) => {
    acc[s] = s === 'all' ? allListings.length : allListings.filter(l => l.status === s).length;
    return acc;
  }, {});

  const finalPrice = () => {
    return parseFloat(pricingForm.platform_price     || 0)
         + parseFloat(pricingForm.platform_charges   || 0)
         + parseFloat(pricingForm.inspection_charges || 0);
  };

  // ── shared styles ──
  const inputStyle = {
    width: '100%', padding: '9px 12px',
    border: '2px solid #e0e0e0', borderRadius: 8,
    fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box',
    outline: 'none',
  };
  const labelStyle = { fontSize: 11, fontWeight: 700, color: '#003566', display: 'block', marginBottom: 4 };
  const actionBtnStyle = (bg, color = '#fff') => ({
    padding: '9px 18px', background: bg, color, border: 'none',
    borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer',
    transition: 'opacity 0.15s', opacity: actionLoading ? 0.6 : 1,
  });

  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', top: 16, right: 16, zIndex: 9999,
          background: toast.type === 'success' ? '#28a745' : '#dc3545',
          color: '#fff', padding: '11px 18px', borderRadius: 8,
          fontWeight: 700, fontSize: 13, boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
        }}>
          {toast.type === 'success' ? '✅ ' : '❌ '}{toast.msg}
        </div>
      )}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <div>
          <h1 style={{ fontSize: 18, fontWeight: 700, color: '#003566', margin: '0 0 2px' }}>🚗 Dealer Car Listings</h1>
          <p style={{ fontSize: 11, color: '#666', margin: 0 }}>Review and publish cars submitted by dealers</p>
        </div>
        <button onClick={load} style={{ padding: '7px 14px', background: '#fff', border: '1px solid #003566', borderRadius: 6, color: '#003566', fontWeight: 700, fontSize: 11, cursor: 'pointer' }}>
          🔄 Refresh
        </button>
      </div>

      {/* Status filters */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
        {FILTERS.map(f => (
          <button key={f} onClick={() => setStatusFilter(f)} style={{
            padding: '5px 12px', borderRadius: 20, fontSize: 11, fontWeight: 700, cursor: 'pointer',
            background: statusFilter === f ? '#003566' : '#f5f5f5',
            color:      statusFilter === f ? '#FFD60A'  : '#555',
            border:     statusFilter === f ? '1px solid #FFD60A' : '1px solid #ddd',
          }}>
            {f.charAt(0).toUpperCase() + f.slice(1)}
            <span style={{
              marginLeft: 5, fontSize: 10, fontWeight: 800,
              background:   statusFilter === f ? '#FFD60A' : '#ddd',
              color:        statusFilter === f ? '#003566' : '#555',
              borderRadius: '50%', width: 16, height: 16,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {counts[f] || 0}
            </span>
          </button>
        ))}
      </div>

      {/* Loading */}
      {loading && (
        <div style={{ textAlign: 'center', padding: '48px 0' }}>
          <div style={{ width: 36, height: 36, border: '4px solid #e0e0e0', borderTopColor: '#003566', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto' }} />
        </div>
      )}

      {/* Table */}
      {!loading && (
        <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e0e0e0', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
            <thead>
              <tr style={{ background: '#003566' }}>
                {['#', 'Car', 'Dealer', 'Ask Price', 'Final Price', 'Status', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '10px 8px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: '#FFD60A', textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {listings.length === 0 ? (
                <tr><td colSpan={7} style={{ padding: '40px', textAlign: 'center', color: '#999' }}>No listings found</td></tr>
              ) : listings.map((l, i) => {
                const sc = STATUS_CONFIG[l.status] || STATUS_CONFIG.draft;
                return (
                  <tr key={l.id} style={{ borderBottom: '1px solid #f0f0f0', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                    <td style={{ padding: '8px', color: '#003566', fontWeight: 700 }}>#{l.id}</td>
                    <td style={{ padding: '8px' }}>
                      <div style={{ fontWeight: 700, color: '#003566', fontSize: 12 }}>{l.year} {l.brand} {l.model}</div>
                      <div style={{ fontSize: 10, color: '#999' }}>{l.fuel_type} · {l.transmission}{l.km_driven ? ` · ${Number(l.km_driven).toLocaleString('en-IN')} km` : ''}</div>
                    </td>
                    <td style={{ padding: '8px' }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: '#333' }}>{l.dealer?.name || '—'}</div>
                      <div style={{ fontSize: 10, color: '#999' }}>{l.dealer?.shop_name}</div>
                      <div style={{ fontSize: 10, color: '#999' }}>{l.dealer?.phone}</div>
                    </td>
                    <td style={{ padding: '8px', fontWeight: 700, color: '#003566' }}>{fmt(l.asking_price)}</td>
                    <td style={{ padding: '8px', fontWeight: 700, color: '#059669' }}>{fmt(l.final_sell_price)}</td>
                    <td style={{ padding: '8px' }}>
                      <span style={{ padding: '3px 8px', borderRadius: 20, fontSize: 10, fontWeight: 700, background: sc.bg, color: sc.color }}>{sc.label}</span>
                    </td>
                    <td style={{ padding: '8px' }}>
                      <button
                        onClick={() => openDetail(l)}
                        style={{ padding: '5px 12px', background: '#003566', color: '#FFD60A', border: '2px solid #FFD60A', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
                      >
                        Manage →
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Detail Modal ── */}
      {selectedListing && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: 16 }}
          onClick={() => setSelectedListing(null)}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 780, maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 80px rgba(0,0,0,0.4)' }}
          >
            {/* Modal Header */}
            <div style={{ background: 'linear-gradient(135deg,#003566,#001d3d)', padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '3px solid #FFD60A' }}>
              <div>
                <h2 style={{ margin: 0, color: '#FFD60A', fontSize: 16, fontWeight: 800 }}>
                  🚗 {selectedListing.year} {selectedListing.brand} {selectedListing.model}
                </h2>
                <p style={{ margin: '3px 0 0', color: 'rgba(255,255,255,0.6)', fontSize: 12 }}>
                  #{selectedListing.id} · {selectedListing.dealer?.name} · {(STATUS_CONFIG[selectedListing.status] || STATUS_CONFIG.draft).label}
                </p>
              </div>
              <button
                onClick={() => setSelectedListing(null)}
                style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 8, color: '#fff', width: 32, height: 32, cursor: 'pointer', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                ✕
              </button>
            </div>

            {/* Modal Tabs */}
            <div style={{ display: 'flex', background: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>
              {[
                { key: 'info',    label: '📋 Info' },
                { key: 'images',  label: '📷 Images' },
                { key: 'actions', label: '⚙️ Actions' },
              ].map(t => (
                <button
                  key={t.key}
                  onClick={() => setDetailTab(t.key)}
                  style={{
                    padding: '11px 20px', border: 'none', background: 'transparent', cursor: 'pointer',
                    fontSize: 13, fontWeight: 700,
                    color:        detailTab === t.key ? '#003566' : '#9ca3af',
                    borderBottom: detailTab === t.key ? '3px solid #003566' : '3px solid transparent',
                  }}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {/* Modal Body */}
            <div style={{ overflowY: 'auto', flex: 1, padding: 20 }}>

              {/* ── INFO TAB ── */}
              {detailTab === 'info' && (
                <div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 20 }}>
                    {[
                      ['Brand',               selectedListing.brand],
                      ['Model',               selectedListing.model],
                      ['Year',                selectedListing.year],
                      ['Fuel Type',           selectedListing.fuel_type],
                      ['Transmission',        selectedListing.transmission],
                      ['Color',               selectedListing.color],
                      ['KM Driven',           selectedListing.km_driven ? `${Number(selectedListing.km_driven).toLocaleString('en-IN')} km` : '—'],
                      ['Reg. Number',         selectedListing.registration_number || '—'],
                      ['Asking Price',        fmt(selectedListing.asking_price)],
                      ['Platform Price',      fmt(selectedListing.platform_price)],
                      ['Platform Charges',    fmt(selectedListing.platform_charges)],
                      ['Inspection Charges',  fmt(selectedListing.inspection_charges)],
                      ['Final Sell Price',    fmt(selectedListing.final_sell_price)],
                      ['Car ID (if created)', selectedListing.car_id ? `#${selectedListing.car_id}` : '—'],
                    ].map(([label, value]) => (
                      <div key={label} style={{ background: '#f8fafc', borderRadius: 9, padding: '12px 14px', border: '1px solid #e5e7eb' }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', marginBottom: 4 }}>{label}</div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#003566' }}>{value || '—'}</div>
                      </div>
                    ))}
                  </div>
                  {selectedListing.description && (
                    <div style={{ background: '#f8fafc', borderRadius: 9, padding: 14, border: '1px solid #e5e7eb' }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', marginBottom: 6 }}>Description</div>
                      <p style={{ margin: 0, fontSize: 13, color: '#374151', lineHeight: 1.6 }}>{selectedListing.description}</p>
                    </div>
                  )}
                  {selectedListing.rejection_reason && (
                    <div style={{ background: '#fff1f1', borderRadius: 9, padding: 14, border: '1px solid #fca5a5', marginTop: 12 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: '#dc2626', textTransform: 'uppercase', marginBottom: 6 }}>Rejection Reason</div>
                      <p style={{ margin: 0, fontSize: 13, color: '#991b1b' }}>{selectedListing.rejection_reason}</p>
                    </div>
                  )}

                  {/* Dealer Info */}
                  <div style={{ marginTop: 16, background: 'linear-gradient(135deg,#003566,#001d3d)', borderRadius: 10, padding: 16 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#FFD60A', marginBottom: 10 }}>👤 Dealer Info</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                      {[
                        ['Name',      selectedListing.dealer?.name],
                        ['Shop',      selectedListing.dealer?.shop_name],
                        ['Phone',     selectedListing.dealer?.phone],
                      ].map(([label, val]) => (
                        <div key={label}>
                          <div style={{ fontSize: 9, color: 'rgba(255,214,10,0.7)', textTransform: 'uppercase', marginBottom: 2 }}>{label}</div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>{val || '—'}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* ── IMAGES TAB ── */}
              {detailTab === 'images' && (
                <div>
                  {(!selectedListing.images || selectedListing.images.length === 0) ? (
                    <div style={{ textAlign: 'center', padding: '60px 20px', color: '#9ca3af' }}>
                      <div style={{ fontSize: 48, marginBottom: 12 }}>📷</div>
                      <p style={{ fontWeight: 600 }}>No images uploaded</p>
                    </div>
                  ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12 }}>
                      {selectedListing.images.map((src, i) => (
                        <div key={i} style={{ borderRadius: 10, overflow: 'hidden', border: '2px solid #e5e7eb', background: '#f8fafc' }}>
                          <img src={src} alt={`Car ${i + 1}`} style={{ width: '100%', height: 140, objectFit: 'cover', display: 'block' }} onError={e => e.target.style.display = 'none'} />
                          <a href={src} target="_blank" rel="noreferrer" style={{ display: 'block', padding: '6px', background: '#f3f4f6', color: '#003566', textAlign: 'center', textDecoration: 'none', fontSize: 11, fontWeight: 600 }}>
                            🔗 View Full
                          </a>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* ── ACTIONS TAB ── */}
              {detailTab === 'actions' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

                  {/* Approve / Reject */}
                  {selectedListing.status === 'submitted' && (
                    <div style={{ background: '#f8fafc', borderRadius: 12, padding: 18, border: '1px solid #e5e7eb' }}>
                      <h3 style={{ margin: '0 0 14px', fontSize: 15, color: '#003566' }}>✅ Review Decision</h3>
                      <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
                        <button
                          onClick={() => handleApprove(selectedListing.id)}
                          disabled={actionLoading}
                          style={actionBtnStyle('#28a745')}
                        >
                          ✅ Approve Listing
                        </button>
                      </div>
                      <div>
                        <label style={labelStyle}>Rejection Reason</label>
                        <textarea
                          value={rejectReason}
                          onChange={e => setRejectReason(e.target.value)}
                          placeholder="Enter reason for rejection..."
                          rows={3}
                          style={{ ...inputStyle, resize: 'none', marginBottom: 10 }}
                        />
                        <button
                          onClick={() => handleReject(selectedListing.id)}
                          disabled={actionLoading}
                          style={actionBtnStyle('#dc3545')}
                        >
                          ❌ Reject Listing
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Set Pricing */}
                  {(selectedListing.status === 'approved' || selectedListing.status === 'published') && (
                    <div style={{ background: '#f8fafc', borderRadius: 12, padding: 18, border: '1px solid #e5e7eb' }}>
                      <h3 style={{ margin: '0 0 14px', fontSize: 15, color: '#003566' }}>💰 Set / Update Pricing</h3>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 14 }}>
                        {[
                          ['Platform Price (₹)',     'platform_price',     'Base price for the car'],
                          ['Platform Charges (₹)',   'platform_charges',   'Commission/service fee'],
                          ['Inspection Charges (₹)', 'inspection_charges', 'Inspection cost'],
                        ].map(([label, field, hint]) => (
                          <div key={field}>
                            <label style={labelStyle}>{label}</label>
                            <input
                              type="number"
                              value={pricingForm[field]}
                              onChange={e => setPricingForm(prev => ({ ...prev, [field]: e.target.value }))}
                              placeholder="0"
                              style={inputStyle}
                            />
                            <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 3 }}>{hint}</div>
                          </div>
                        ))}
                      </div>
                      <div style={{ background: 'linear-gradient(135deg,#003566,#001d3d)', borderRadius: 9, padding: '12px 16px', marginBottom: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', fontWeight: 600 }}>Calculated Final Price</span>
                        <span style={{ fontSize: 18, fontWeight: 800, color: '#FFD60A' }}>{fmt(finalPrice())}</span>
                      </div>
                      <button
                        onClick={() => handleSetPricing(selectedListing.id)}
                        disabled={actionLoading}
                        style={actionBtnStyle('linear-gradient(135deg,#003566,#001d3d)', '#FFD60A')}
                      >
                        💾 Save Pricing
                      </button>
                    </div>
                  )}

                  {/* Create Car */}
                  {selectedListing.status === 'approved' && !selectedListing.car_id && (
                    <div style={{ background: '#f0fdf4', borderRadius: 12, padding: 18, border: '1px solid #86efac' }}>
                      <h3 style={{ margin: '0 0 8px', fontSize: 15, color: '#166534' }}>🚗 Create Car Record</h3>
                      <p style={{ margin: '0 0 14px', fontSize: 13, color: '#166534' }}>
                        Creates a Car entry from this dealer listing so you can add specs, features, and publish it.
                      </p>
                      <button
                        onClick={() => handleCreateCar(selectedListing.id)}
                        disabled={actionLoading}
                        style={actionBtnStyle('#16a34a')}
                      >
                        🚗 Create Car Record
                      </button>
                    </div>
                  )}

                  {/* Car already created */}
                  {selectedListing.car_id && (
                    <div style={{ background: '#dbeafe', borderRadius: 12, padding: 18, border: '1px solid #93c5fd' }}>
                      <h3 style={{ margin: '0 0 6px', fontSize: 15, color: '#1d4ed8' }}>✅ Car Record Created</h3>
                      <p style={{ margin: '0 0 14px', fontSize: 13, color: '#1d4ed8' }}>
                        Car #{selectedListing.car_id} was created. Go to Cars section to add specifications, features, and publish.
                      </p>
                    </div>
                  )}

                  {/* Publish */}
                  {selectedListing.status === 'approved' && selectedListing.car_id && (
                    <div style={{ background: '#fef3c7', borderRadius: 12, padding: 18, border: '1px solid #fcd34d' }}>
                      <h3 style={{ margin: '0 0 8px', fontSize: 15, color: '#92400e' }}>🚀 Publish Car</h3>
                      <p style={{ margin: '0 0 14px', fontSize: 13, color: '#92400e' }}>
                        Make sure Car #{selectedListing.car_id} has all specs and content verified before publishing.
                      </p>
                      <button
                        onClick={() => handlePublish(selectedListing.id)}
                        disabled={actionLoading}
                        style={actionBtnStyle('#d97706')}
                      >
                        🚀 Publish Now
                      </button>
                    </div>
                  )}

                  {/* Already published */}
                  {selectedListing.status === 'published' && (
                    <div style={{ background: '#dcfce7', borderRadius: 12, padding: 18, border: '1px solid #86efac', textAlign: 'center' }}>
                      <p style={{ fontSize: 32, margin: '0 0 8px' }}>🎉</p>
                      <h3 style={{ margin: '0 0 4px', color: '#166534', fontSize: 15 }}>Car is Live!</h3>
                      <p style={{ margin: 0, fontSize: 13, color: '#166534' }}>This car is published and visible to buyers.</p>
                    </div>
                  )}

                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}